import styled, { css } from "styled-components";
import { Disclosure, Button, DisclosurePanel } from "react-aria-components";
import { useDesignTokens } from "@boi/react-design-tokens";
import { CoreAccordion } from "@boi/components-core";

type ContainerProps = {
  $variant: CoreAccordion.Variant;
};

export const Container = styled(Disclosure)<ContainerProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { accordion } = tokens;

    return [
      css`
        display: grid;
        background: ${accordion.background[$variant]};
        border-width: ${accordion.stroke.width};
        border-style: ${accordion.stroke.style};
        border-color: ${accordion.stroke.color};
        border-radius: ${accordion.stroke.radius};
      `,
    ];
  }}
`;

type HeaderProps = {
  $variant: CoreAccordion.Variant;
};

export const Header = styled(Button)<HeaderProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { accordion } = tokens;

    return [
      css`
        all: unset;
        box-sizing: border-box;
        position: relative;
        display: grid;
        min-height: ${accordion.header.minHeight[$variant]};
        padding: ${accordion.header.padding};
        color: ${accordion.header.text.color.base};
        text-decoration: ${accordion.header.text.decoration.line.base};
        text-underline-offset: ${accordion.header.text.decoration.offset};
        cursor: pointer;

        &[data-focus-visible] {
          border-radius: ${accordion.header.focusRing.radius};
          outline-width: ${accordion.header.focusRing.width};
          outline-style: ${accordion.header.focusRing.style};
          outline-color: ${accordion.header.focusRing.color};
          outline-offset: ${accordion.header.focusRing.offset};
        }

        &[data-hovered] {
          color: ${accordion.header.text.color.hover};
          text-decoration: ${accordion.header.text.decoration.line.hover};
        }

        &[data-pressed] {
          color: ${accordion.header.text.color.active};
          text-decoration: ${accordion.header.text.decoration.line.active};
        }
      `,
    ];
  }}
`;

type HeaderInnerProps = {
  $variant: CoreAccordion.Variant;
};

export const HeaderInner = styled.div<HeaderInnerProps>`
  ${({ $variant }) => {
    const { tokens } = useDesignTokens();
    const { accordion } = tokens;

    return [
      css`
        display: grid;
        gap: ${accordion.header.inner.gap};
        align-items: center;
        grid-template-columns: 1fr auto;
        padding-top: ${accordion.header.inner.padding.top[$variant]};
        padding-left: ${accordion.header.inner.padding.left};
        padding-right: ${accordion.header.inner.padding.right};
        padding-bottom: ${accordion.header.inner.padding.bottom[$variant]};
        border-radius: ${accordion.header.inner.radius};
        background: ${accordion.header.inner.backgroundColor.base};

        ${Header}[data-hovered] & {
          background: ${accordion.header.inner.backgroundColor.hover};
        }

        ${Header}[data-pressed] & {
          background: ${accordion.header.inner.backgroundColor.active[
            $variant
          ]};
        }
      `,
    ];
  }}
`;
type HeaderIconWrapperProps = {
  $isExpanded: boolean;
};

export const HeaderIconWrapper = styled.div<HeaderIconWrapperProps>`
  ${({ $isExpanded }) => {
    const { tokens } = useDesignTokens();
    const { accordion } = tokens;

    return [
      css`
        color: ${accordion.header.icon.color.base};
        transform: rotateX(${$isExpanded ? "180deg" : "0deg"});

        svg {
          display: block;
          width: ${accordion.header.icon.width};
          height: ${accordion.header.icon.height};
        }

        ${Header}[data-hovered] & {
          color: ${accordion.header.icon.color.hover};
        }

        ${Header}[data-pressed] & {
          color: ${accordion.header.icon.color.active};
        }
      `,
    ];
  }}
`;

type SeparatorProps = {
  $isExpanded: boolean;
};

export const Separator = styled.div<SeparatorProps>`
  ${({ $isExpanded }) => {
    const { tokens } = useDesignTokens();
    const { accordion } = tokens;

    return [
      css`
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        display: ${$isExpanded ? "block" : "none"};
        height: ${accordion.separator.stroke.width};
        margin-left: ${accordion.separator.margin.left};
        margin-right: ${accordion.separator.margin.right};
        border-top-width: ${accordion.separator.stroke.width};
        border-top-style: ${accordion.separator.stroke.style};
        border-top-color: ${accordion.separator.stroke.color};
      `,
    ];
  }}
`;

type ContentProps = {
  $isExpanded: boolean;
};

export const Content = styled(DisclosurePanel)<ContentProps>`
  ${({ $isExpanded }) => {
    const { tokens } = useDesignTokens();
    const { accordion } = tokens;

    return [
      $isExpanded &&
        css`
          padding: ${accordion.content.padding};
        `,
    ];
  }}
`;
